import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import MobileTopSearchbar from "@modules/search/mobile-top-searchbar";
import { EXPLORE_SLUG } from "types/api/explore";
import useSearchExperiments from "@shared/hooks/useSearchExperiments";
const SearchPanel = dynamic(() => import("@modules/search/search-panel"), {
  ssr: false,
});

interface Props {
  isAdmin?: boolean;
  open?: boolean;
}

const Search: React.FC<Props> = ({ isAdmin = false, open = false }) => {
  const { asPath, query, push, pathname, isReady } = useRouter();
  const searchExperimentSubmitter = useSearchExperiments();

  const [value, setValue] = useState("");
  const searchString = "gsc.q=";

  const isExplorePage = pathname.includes("/explore");

  const isRedirectToExplore =
    process.env.NEXT_PUBLIC_SHOULD_REDIRECT_SEARCH_TO_EXPLORE === "true";

  const exploreTerm =
    typeof query.term === "string"
      ? query.term
      : Array.isArray(query.term)
        ? (query.term[0] ?? "")
        : "";

  const searchQuery = query?.q
    ? asPath.includes(searchString)
      ? decodeURIComponent(String(asPath).split(searchString)[1]?.split("&")[0])
      : decodeURIComponent(String(query.q).replace(/%E2%80%8E/g, ""))
    : "";

  useEffect(() => {
    if (!isReady) return;

    if (
      isExplorePage &&
      isRedirectToExplore &&
      query.slug !== EXPLORE_SLUG.LANDING
    ) {
      setValue(exploreTerm);
      return;
    }

    setValue(searchQuery);
  }, [isReady, exploreTerm, searchQuery, isExplorePage, isRedirectToExplore]);

  const handlePopState = (e: PopStateEvent) => {
    const theTarget = e.target as Window;
    if (asPath.startsWith("/search") && theTarget?.location!.search !== "") {
      const search = theTarget?.location!.search.split("?")[1].split("&");
      var tup = search.map((d: string) => d.split("="))?.[1] || [];
      if (
        tup &&
        tup[0] === "q" &&
        decodeURIComponent(tup[1]) !==
          decodeURIComponent(
            String(theTarget.location.hash).split("gsc.q=")[1]?.split("&")[0]
          )
      ) {
        document.title = `نتایج جستجو: ${decodeURIComponent(
          String(theTarget.location.hash).split("gsc.q=")[1]?.split("&")[0]
        )} | فرادرس`;
        setValue(
          decodeURIComponent(
            String(theTarget.location.hash).split("gsc.q=")[1]?.split("&")[0]
          )
        );
      } else {
        document.title = `نتایج جستجو: ${decodeURIComponent(tup[1])} | فرادرس`;
        setValue(query.q ? decodeURIComponent(tup[1]) : "");
      }
    } else if (theTarget?.location?.search !== "") {
      push(theTarget.location.pathname + theTarget.location.search);
    }
  };

  const onAction = (term: string) => {
    if (!term) return;

    searchExperimentSubmitter(term);
    setValue(term);

    const encodedTerm = encodeURIComponent(term).replace(/%20/g, "+");

    if (asPath.startsWith("/search")) {
      document.title = `نتایج جستجو: ${term} - فرادرس`;

      if (query.q) {
        const searchElement = window.google?.search?.cse?.element?.getElement(
          "searchresults-only0"
        );

        if (searchElement) {
          searchElement.execute(term);
          return;
        }

        push(`/search?ref=header_search&q=${encodedTerm}`);
      }

      return;
    }

    if (isExplorePage && isRedirectToExplore) {
      const params = new URLSearchParams({
        ...query,
        term,
        page: "1",
      });

      push(`/explore?${params.toString()}`);
      return;
    }

    push(`/search?ref=header_search&q=${encodedTerm}`);
  };

  const searchProps = {
    resetValue: () => setValue(""),
    onChange: (e: string) => e !== "" && setValue(e),
    openSearchSidebar: open,
    isAdmin: isAdmin,
    onAction,
    value,
    where: "header",
  };

  useEffect(() => {
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <>
      <div className="hidden md:block search-panel-sizing ml-auto">
        <SearchPanel {...searchProps} />
      </div>
      <div className="md:hidden ml-auto grow">
        <MobileTopSearchbar {...searchProps} />
      </div>
    </>
  );
};

export default Search;
